// MonthSelector (DESIGN §7, client workspace): the month strip — compact 01…NN cells (NN ≤ 99), each with the batch
// living in it as a tiny label ("B6"), a status bar (Upcoming gray · Active lime · Completed green · Off red — the
// whole cell turns red), the current month marked with a dot, prev/next steppers, a hairline after every 12 months.
// The track scrolls horizontally when it does not fit and keeps the selected cell in view.
// Tabs pattern: only the selected cell is in the tab order; ←/→ move, Home/End jump to the first/last month.
import './month-selector.css';
import { useLayoutEffect, useRef } from 'preact/hooks';
import { Icon } from '../icons.jsx';
import { monthLabel, pad2 } from '../../shared/constants.js';
import { cx } from './ui.jsx';

// The month whose date range contains `today`, or 0 before the first / after the last month.
// months: [{ monthNumber, startDate, endDate }].
export function nowMonthNumber(months, today) {
  const m = (months || []).find((x) => today >= x.startDate && today <= x.endDate);
  return m ? m.monthNumber : 0;
}

const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Stepper that never disables itself under focus (A11Y-12): aria-disabled + ignored clicks.
function Step({ dir, disabled, onStep }) {
  const label = dir < 0 ? 'Previous month' : 'Next month';
  return (
    <button
      type="button"
      class="icon-btn icon-btn-sm ms-step"
      aria-label={label}
      title={label}
      aria-disabled={disabled ? 'true' : undefined}
      onClick={() => { if (!disabled) onStep(dir); }}
    >
      <Icon name={dir < 0 ? 'chevronLeft' : 'chevronRight'} size={14} />
    </button>
  );
}

// months: [{ monthNumber, status: 'Upcoming'|'Active'|'Completed'|'Off', batches?, tag? /* 'B6' */, tagTitle? }].
// selected / nowMonth (0 = none): month numbers.
function markEdges(track) {
  const max = track.scrollWidth - track.clientWidth;
  const start = track.scrollLeft > 2;
  const end = max > 2 && track.scrollLeft < max - 2;
  const fade = start && end ? 'both' : start ? 'start' : end ? 'end' : '';
  if ((track.dataset.fade || '') !== fade) track.dataset.fade = fade;
}

// onSelect(n). controls: id of the tabpanel the strip controls. idPrefix: tab ids are `${idPrefix}-tab-${n}`.
export function MonthSelector({ months = [], selected, nowMonth = 0, onSelect, label = 'Months', idPrefix = 'ms', controls, class: cls }) {
  const trackRef = useRef(null);
  const focusNext = useRef(false);
  const mounted = useRef(false);
  const count = months.length;

  useLayoutEffect(() => {
    const track = trackRef.current;
    const cell = track?.querySelector(`[data-month="${selected}"]`);
    if (!track || !cell) return;
    if (track.scrollWidth > track.clientWidth + 1) {
      const left = Math.max(0, cell.offsetLeft - (track.clientWidth - cell.offsetWidth) / 2);
      const behavior = mounted.current && !reducedMotion() ? 'smooth' : 'auto';
      if (typeof track.scrollTo === 'function') track.scrollTo({ left, behavior });
      else track.scrollLeft = left;
    }
    if (focusNext.current) {
      focusNext.current = false;
      cell.focus({ preventScroll: true });
    }
    mounted.current = true;
    markEdges(track);
  }, [selected, count]);

  // Which edges hide more months ('start' | 'end' | 'both' | '') → data-fade (CSS fades that edge).
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const on = () => markEdges(track);
    on();
    track.addEventListener('scroll', on, { passive: true });
    window.addEventListener('resize', on);
    return () => { track.removeEventListener('scroll', on); window.removeEventListener('resize', on); };
  }, []);

  const go = (n, focus = false) => {
    const target = Math.min(Math.max(n, 1), count);
    if (target === selected) return;
    focusNext.current = focus;
    onSelect?.(target);
  };

  const onKeyDown = (e) => {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    const next = { ArrowLeft: selected - 1, ArrowRight: selected + 1, Home: 1, End: count }[e.key];
    if (next === undefined) return;
    e.preventDefault();
    go(next, true);
  };

  return (
    <div class={cx('ms', cls)}>
      <Step dir={-1} disabled={selected <= 1} onStep={(d) => go(selected + d)} />
      <div class="ms-track" ref={trackRef} role="tablist" aria-label={label} onKeyDown={onKeyDown}>
        {months.map((m) => {
          const n = m.monthNumber;
          const isSel = n === selected;
          const isNow = n === nowMonth;
          const name = [monthLabel(n), m.status, isNow && 'current month', m.tagTitle || (m.batches != null && `${m.batches} ${m.batches === 1 ? 'batch' : 'batches'}`)]
            .filter(Boolean).join(', ');
          return (
            <button
              key={n}
              type="button"
              role="tab"
              id={`${idPrefix}-tab-${n}`}
              data-month={n}
              class={cx('ms-cell', `is-${String(m.status).toLowerCase()}`, isSel && 'is-selected', isNow && 'is-now', n > 1 && n % 12 === 1 && 'is-year')}
              aria-selected={isSel ? 'true' : 'false'}
              aria-controls={isSel ? controls : undefined}
              aria-label={name}
              title={name.replace(/, /g, ' · ')}
              tabIndex={isSel ? 0 : -1}
              onClick={() => go(n)}
            >
              <span class="ms-num" aria-hidden="true">{pad2(n)}</span>
              <span class="ms-tag" aria-hidden="true">{m.tag || ''}</span>
              <span class="ms-bar" aria-hidden="true" />
              {isNow && <span class="ms-now" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
      <Step dir={1} disabled={selected >= count} onStep={(d) => go(selected + d)} />
    </div>
  );
}
